import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight, Check, Download, Heart, Calendar, Utensils, FileText,
  Shield, Users, BookOpen, MessageCircle, Play,
  Gift, Zap, Search, Award
} from "lucide-react";
import axios from "axios";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [packages, setPackages] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const pricingRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/packages`).then(r => setPackages(r.data.packages)).catch(() => {});
    const interval = setInterval(() => setActiveTestimonial(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: "smooth" });

  const testimonials = [
    { name: "Sarah M.", before: "I could not leave the house without a panic attack. He had me believing I was worthless.", after: "Six months in, I started my own business. I wake up excited about life again.", tag: "Starter Kit" },
    { name: "David R.", before: "She controlled every aspect of my life. I did not even know who I was anymore.", after: "The Pattern Detector showed me exactly what she was doing. Now I help other men in my support group.", tag: "Growth Bundle" },
    { name: "Keisha L.", before: "Two narcissistic relationships back to back. I thought something was fundamentally wrong with me.", after: "The e-novella made me cry, but it also made me realize I was not alone. I am dating a kind man now.", tag: "Premium" },
    { name: "Marcus J.", before: "I was told real men do not get abused. I suffered in silence for 12 years.", after: "ThryvLife gave me the language to describe what happened and the courage to heal.", tag: "Growth Bundle" },
  ];

  const freeStuff = [
    { icon: MessageCircle, title: "AI Recovery Companion (Hope)", desc: "24/7 AI support companion. She listens, validates, and helps you process. Completely free." },
    { icon: FileText, title: "15 Ways to Stop a Narcissist", desc: "Our signature PDF guide with proven strategies you can use today." },
    { icon: Calendar, title: "Daily Affirmation Calendar", desc: "14 affirmations with real survivor testimonies and wisdom from philosophers." },
    { icon: Shield, title: "Arkansas Emergency Resources", desc: "Complete directory of crisis hotlines, state laws, and federal protections." },
  ];

  const tierColors = {
    starter: { bg: "from-[#4FB3D4]/5 to-[#4FB3D4]/10", border: "border-[#4FB3D4]/30", accent: "text-[#4FB3D4]", btn: "bg-[#4FB3D4] hover:bg-[#4FB3D4]/90" },
    growth: { bg: "from-[#A69BE0]/5 to-[#A69BE0]/15", border: "border-[#A69BE0]/40", accent: "text-[#A69BE0]", btn: "bg-[#A69BE0] hover:bg-[#A69BE0]/90" },
    premium: { bg: "from-[#FFB7B2]/5 to-[#FFB7B2]/15", border: "border-[#FFB7B2]/40", accent: "text-[#FFB7B2]", btn: "bg-[#1A2A3A] hover:bg-[#1A2A3A]/90" },
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />

      {/* HERO — Clean, stylish, image-driven */}
      <section className="pt-16 relative overflow-hidden" data-testid="hero-section">
        <div className="relative h-[480px] md:h-[520px]">
          <img
            src="https://images.unsplash.com/photo-1702075299109-d34420d22502?w=1600&q=80"
            alt="Serene ocean sunrise representing hope and new beginnings"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-[#1A2A3A]/40 to-[#1A2A3A]/60" />
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
            <p className="text-white/80 text-sm font-medium uppercase tracking-[0.2em] mb-4" data-testid="hero-tagline">
              Life grows in inches. We Thryv in miles.
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight max-w-3xl" data-testid="hero-heading">
              Here Is <span className="text-[#7AD3F0]">Everything</span> You Get
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-xl mb-8">
              Digital products, an AI companion, real survivor stories, and legal resources. Built by Brian Bass — someone who has been exactly where you are.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={scrollToPricing} className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white px-8 py-5 rounded-full font-semibold shadow-lg text-base" data-testid="hero-cta-btn">
                See the Plans <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Link to="/chat">
                <Button variant="outline" className="rounded-full px-8 py-5 text-base font-medium border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm" data-testid="hero-chat-btn">
                  <MessageCircle className="mr-2 h-5 w-5" /> Talk to Hope (Free)
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-16 px-6 lg:px-12 -mt-12 relative z-20" data-testid="products-section">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: BookOpen, title: "E-Novella", subtitle: "\"Sunshine After the Rain\"", desc: "4 powerful stories of survival", color: "bg-[#A69BE0]/15", iconColor: "text-[#A69BE0]" },
              { icon: Search, title: "Pattern Detector", subtitle: "AI Manipulation Scanner", desc: "Analyze texts for narcissistic tactics", color: "bg-[#FFB7B2]/15", iconColor: "text-[#FFB7B2]" },
              { icon: MessageCircle, title: "AI Companion", subtitle: "Meet Hope", desc: "24/7 empathetic support", color: "bg-[#4FB3D4]/10", iconColor: "text-[#4FB3D4]" },
              { icon: Shield, title: "Legal Resources", subtitle: "AR State & Federal Laws", desc: "Know your rights", color: "bg-[#4FB3D4]/10", iconColor: "text-[#4FB3D4]" },
            ].map((item, i) => (
              <Card key={i} className="bg-white rounded-2xl shadow-card border border-[#E5E7EB]/50 p-6 hover:shadow-floating transition-all duration-300 hover:-translate-y-1" data-testid={`product-card-${i}`}>
                <CardContent className="p-0">
                  <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4`}>
                    <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-1">{item.title}</h3>
                  <p className="text-sm font-medium text-[#4FB3D4] mb-2">{item.subtitle}</p>
                  <p className="text-sm text-[#1A2A3A]/60">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Free Stuff */}
      <section className="py-16 px-6 lg:px-12 bg-white" data-testid="free-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Gift className="h-4 w-4" /> 100% Free, No Strings
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A2A3A] mb-3">
              We Believe Healing Should Start for Free
            </h2>
            <p className="text-base text-[#1A2A3A]/60 max-w-xl mx-auto">
              I have been where you are. That is why these tools cost nothing. No credit card. No catch. — Brian Bass
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {freeStuff.map((item, i) => (
              <div key={i} className="flex gap-4 p-5 bg-[#FDFBF7] rounded-2xl border border-[#E5E7EB]/50 hover:shadow-card transition-all" data-testid={`free-item-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#4FB3D4]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="h-5 w-5 text-[#4FB3D4]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A2A3A] mb-1 text-sm">{item.title}</h3>
                  <p className="text-sm text-[#1A2A3A]/60">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/bonus"><Button variant="outline" className="rounded-full px-6 py-4 border-2 border-[#4FB3D4]/20 hover:bg-[#4FB3D4]/5 text-sm font-medium" data-testid="free-pdf-btn"><Download className="mr-2 h-4 w-4" /> Download Free PDF</Button></Link>
            <Link to="/chat"><Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 text-sm font-medium" data-testid="free-chat-btn"><MessageCircle className="mr-2 h-4 w-4" /> Chat with Hope</Button></Link>
          </div>
        </div>
      </section>

      {/* Before/After */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-[#FDFBF7] to-[#DCD6F7]/15" data-testid="testimonials-section">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-3">Real People, Real Recovery</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A2A3A]">Before & After</h2>
          </div>
          <Card className="bg-white rounded-3xl shadow-floating overflow-hidden" data-testid="testimonial-card">
            <CardContent className="p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-5 bg-red-50/50 rounded-2xl border-l-4 border-red-300">
                  <p className="text-xs font-semibold uppercase tracking-wider text-red-400 mb-2">Before</p>
                  <p className="text-[#1A2A3A]/70 italic text-sm leading-relaxed">"{testimonials[activeTestimonial].before}"</p>
                </div>
                <div className="p-5 bg-green-50/50 rounded-2xl border-l-4 border-green-400">
                  <p className="text-xs font-semibold uppercase tracking-wider text-green-500 mb-2">After</p>
                  <p className="text-[#1A2A3A]/80 italic text-sm leading-relaxed">"{testimonials[activeTestimonial].after}"</p>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-[#1A2A3A] text-sm">{testimonials[activeTestimonial].name}</p>
                  <p className="text-xs text-[#4FB3D4]">{testimonials[activeTestimonial].tag}</p>
                </div>
                <div className="flex gap-2">
                  {testimonials.map((_, i) => (
                    <button key={i} onClick={() => setActiveTestimonial(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === activeTestimonial ? "bg-[#4FB3D4] w-7" : "bg-[#E5E7EB]"}`} data-testid={`testimonial-dot-${i}`} />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          <p className="text-center text-[10px] text-[#1A2A3A]/40 mt-3 italic">Names changed for privacy. We do not promise to cure any disease or mental illness.</p>
        </div>
      </section>

      {/* Video Section — Real YouTube Content */}
      <section className="py-16 px-6 lg:px-12 bg-white" data-testid="video-section">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <p className="text-sm font-medium uppercase tracking-widest text-[#4FB3D4] mb-3">Watch & Learn</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A2A3A] mb-3">Recovery Videos from Trusted Experts</h2>
            <p className="text-sm text-[#1A2A3A]/60 max-w-xl mx-auto">Knowledge is power. These videos from Dr. Ramani and other experts help you recognize the patterns and take back your life.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { id: "l_2TXdoTCvs", title: "How to Heal After Narcissistic Relationships", src: "Dr. Ramani", dur: "45 min" },
              { id: "FioXRf_VILM", title: "The Ultimate Guide to Overcoming Narcissistic Abuse", src: "DoctorRamani", dur: "37 min" },
              { id: "G1g5nCPf5a4", title: "Self-Care Recovery After a Narcissistic Relationship", src: "DoctorRamani", dur: "40 min" },
            ].map((v, i) => (
              <a key={i} href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer" data-testid={`video-card-${i}`}>
                <Card className="bg-[#FDFBF7] rounded-2xl overflow-hidden hover:shadow-floating transition-all group">
                  <div className="relative h-40">
                    <img src={`https://img.youtube.com/vi/${v.id}/mqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="h-5 w-5 text-[#4FB3D4] ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <p className="text-[10px] text-[#A69BE0] font-medium">{v.src} | {v.dur}</p>
                    <h3 className="font-heading text-sm font-semibold text-[#1A2A3A] mt-1 line-clamp-2">{v.title}</h3>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/community"><Button variant="outline" className="rounded-full px-6 py-3 text-sm border-2 border-[#4FB3D4]/20 hover:bg-[#4FB3D4]/5" data-testid="see-all-videos-btn">See All Videos in Community Hub</Button></Link>
          </div>
        </div>
      </section>
      <section className="py-16 px-6 lg:px-12 bg-white" data-testid="pattern-section">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FFB7B2]/15 text-[#1A2A3A] px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
                <Zap className="h-3 w-3 text-[#FFB7B2]" /> Exclusive to Growth & Premium
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A2A3A] mb-4">Pattern Detector Bot</h2>
              <p className="text-[#1A2A3A]/60 leading-relaxed mb-5">
                Paste any text conversation and our AI instantly identifies narcissistic manipulation patterns — gaslighting, DARVO, love bombing, triangulation, and more. Nobody else offers this.
              </p>
              <ul className="space-y-3 mb-6">
                {["Identifies specific manipulation tactics with evidence", "Explains WHY each phrase is manipulative", "Suggests responses using Grey Rock & boundary techniques", "Gives a severity score: Green / Yellow / Red"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#1A2A3A]/70">
                    <Check className="h-4 w-4 text-[#4FB3D4] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Button onClick={scrollToPricing} className="bg-[#A69BE0] hover:bg-[#A69BE0]/90 text-white rounded-full px-6 py-4 font-semibold" data-testid="pattern-cta">
                Get Pattern Detector <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="bg-[#FDFBF7] rounded-2xl border border-[#E5E7EB]/50 p-6 shadow-card">
              <div className="space-y-3">
                <div className="bg-white rounded-xl p-4 border border-[#E5E7EB]/50">
                  <p className="text-xs font-semibold text-[#1A2A3A]/40 mb-2">Sample Conversation:</p>
                  <p className="text-sm text-[#1A2A3A]/70 italic">"You are being ridiculous. That never happened. I think you need help because you are imagining things again."</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-xs font-bold text-red-600">RED — Clear Manipulation Detected</span>
                  </div>
                  <p className="text-xs text-red-700"><strong>Gaslighting:</strong> "That never happened" — denying your reality</p>
                  <p className="text-xs text-red-700 mt-1"><strong>Projection:</strong> "You need help" — deflecting blame onto you</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* E-Novella */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-[#1A2A3A] to-[#2D4356]" data-testid="enovella-section">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-[#4FB3D4] mb-3">Included in Growth & Premium</p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">"Sunshine After the Rain"</h2>
              <p className="text-white/70 leading-relaxed mb-6">Four stories. Three happy endings. One tragedy. Written with raw honesty by Brian Bass, because we owe you the truth.</p>
              <ul className="space-y-2 mb-6">
                {["The Charmer — Love bombing unmasked", "The Silent Treatment — Silence as violence", "The Helper — Covert narcissism revealed", "The Unfinished Letter — Why this work matters"].map((s, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80 text-sm"><BookOpen className="h-3 w-3 text-[#4FB3D4] flex-shrink-0" /> {s}</li>
                ))}
              </ul>
              <Button onClick={scrollToPricing} className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-semibold" data-testid="enovella-cta">
                Get the E-Novella <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="flex justify-center">
              <div className="w-56 h-72 bg-gradient-to-br from-[#DCD6F7]/20 to-[#4FB3D4]/10 rounded-2xl shadow-2xl flex flex-col items-center justify-center p-6 border border-white/10">
                <BookOpen className="h-14 w-14 text-[#DCD6F7] mb-3" />
                <p className="font-heading text-lg font-bold text-white text-center mb-1">Sunshine After the Rain</p>
                <p className="text-xs text-white/50 text-center">By Brian Bass (BriVonB)</p>
                <p className="text-[10px] text-[#4FB3D4] mt-3">4 Stories | PDF</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brian Bass Personal */}
      <section className="py-16 px-6 lg:px-12 bg-[#FDFBF7]" data-testid="personal-section">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="w-28 h-28 rounded-2xl overflow-hidden shadow-card flex-shrink-0" data-testid="profile-image">
              <img src="https://customer-assets.emergentagent.com/job_2eed5c8c-c903-4cd4-a6ee-5dc5c12a3bca/artifacts/oyfd26yh_IMG_5012.jpeg" alt="Brian Bass - BriVonB" className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-2">A Note from Brian Bass (BriVonB)</p>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1A2A3A] mb-4">
                I Am Not a Perfect Person. I Am a Real One.
              </h2>
              <div className="space-y-3 text-sm text-[#1A2A3A]/70 leading-relaxed">
                <p>If you are here, you are already brave. I am Brian Bass, and I am still growing and learning every single day. I survived two back-to-back narcissistic relationships. The cheating. The manipulation. The lies that made me question my own sanity.</p>
                <p>My recovery started on a bar bench in the country, building my mother a home. I tore into an old desktop computer, taught myself to build from nothing, went from playing music in Hot Springs, Arkansas to studying affiliate marketing, then AI showed up — and I kept at it. Now I build apps, websites, digital products, chatbots, and tools that actually help people.</p>
                <p>I built ThryvLife because a cord of three strands is not easily broken. Alone, we snap. Together, we are unstoppable. <strong className="text-[#1A2A3A]">Incremental steps. Exponential Thryving.</strong></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-14 px-6 lg:px-12 bg-white" data-testid="mission-section">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="text-sm font-medium uppercase tracking-widest text-[#4FB3D4] mb-3">Our Mission</p>
          <p className="font-heading text-xl md:text-2xl text-[#1A2A3A] leading-relaxed italic">
            "A cord of three strands is not easily broken, but alone, even the strongest thread snaps. We believe in unity in numbers, the strength of community, and the unstoppable force of people who refuse to be silenced."
          </p>
          <p className="text-[#4FB3D4] font-semibold mt-4">— ThryvLife</p>
        </div>
      </section>

      {/* Pricing */}
      <section ref={pricingRef} className="py-16 px-6 lg:px-12 bg-gradient-to-br from-[#FDFBF7] to-[#DCD6F7]/10" data-testid="pricing-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-sm font-medium uppercase tracking-widest text-[#4FB3D4] mb-3">Choose Your Path</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#1A2A3A] mb-3">Invest in the Most Important Person: You</h2>
            <p className="text-sm text-[#1A2A3A]/60 max-w-xl mx-auto">Every plan includes free tools. Cancel anytime.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {packages.map((pkg) => {
              const colors = tierColors[pkg.id] || tierColors.starter;
              const isPopular = pkg.id === "growth";
              return (
                <Card key={pkg.id} className={`relative bg-gradient-to-br ${colors.bg} rounded-2xl border-2 ${colors.border} overflow-hidden transition-all hover:shadow-floating ${isPopular ? "md:-translate-y-2 shadow-floating" : "shadow-card"}`} data-testid={`pricing-${pkg.id}`}>
                  {pkg.tag && (
                    <div className={`absolute top-0 right-0 ${isPopular ? "bg-[#A69BE0]" : pkg.id === "premium" ? "bg-[#1A2A3A]" : "bg-[#4FB3D4]"} text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl`}>{pkg.tag}</div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-1">{pkg.name}</h3>
                    <p className="text-xs text-[#1A2A3A]/60 mb-3">{pkg.description}</p>
                    <div className="flex items-baseline gap-1 mb-5">
                      <span className="font-heading text-4xl font-bold text-[#1A2A3A]">${pkg.price}</span>
                      <span className="text-[#1A2A3A]/50 text-xs">/month</span>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {pkg.features?.map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className={`h-4 w-4 ${colors.accent} flex-shrink-0 mt-0.5`} />
                          <span className="text-xs text-[#1A2A3A]/70">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={`/checkout?plan=${pkg.id}`}>
                      <Button className={`w-full ${colors.btn} text-white rounded-full py-4 font-semibold text-sm`} data-testid={`buy-${pkg.id}-btn`}>
                        {pkg.id === "premium" ? "Go Premium" : pkg.id === "growth" ? "Get the Bundle" : "Start Healing"}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-center text-[10px] text-[#1A2A3A]/40 mt-5">Cancel anytime. All products digital. We do not promise to cure any disease or mental illness.</p>
        </div>
      </section>

      {/* Essay Contest CTA */}
      <section className="py-14 px-6 lg:px-12 bg-gradient-to-br from-[#FFB7B2]/10 to-[#A69BE0]/10" data-testid="essay-cta-section">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row items-center gap-8 bg-white rounded-2xl shadow-card p-8 border border-[#FFB7B2]/20">
            <div className="w-16 h-16 rounded-2xl bg-[#FFB7B2]/15 flex items-center justify-center flex-shrink-0">
              <Award className="h-8 w-8 text-[#FFB7B2]" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-xl font-semibold text-[#1A2A3A] mb-2">Write Your Story. Win Free Access.</h3>
              <p className="text-sm text-[#1A2A3A]/60">
                Three people will receive the complete Recovery Starter Kit ($37 value) + all bonuses for FREE by writing an ~8,000 word essay about their recovery journey. Plus, every participant gets a $10 discount on any upgrade. Your story could help save a life.
              </p>
            </div>
            <Link to="/essay-contest">
              <Button className="bg-[#FFB7B2] hover:bg-[#FFB7B2]/90 text-white rounded-full px-6 py-4 font-semibold whitespace-nowrap" data-testid="essay-cta-btn">
                Enter Contest <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* $10 Discount Banner */}
      <section className="py-8 px-6 lg:px-12 bg-[#1A2A3A]" data-testid="discount-banner">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-white/80 text-sm font-medium mb-2">Use code <span className="bg-[#4FB3D4] text-white px-3 py-1 rounded-full font-bold text-xs mx-1">THRYV10</span> for $10 off any Growth or Premium plan</p>
          <p className="text-white/50 text-xs">Growth: $47 &rarr; $37 | Premium: $67 &rarr; $57 | Lifetime Growth: $85 &rarr; $75 | Lifetime Premium: $100 &rarr; $90</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6 lg:px-12 bg-[#1A2A3A]" data-testid="final-cta">
        <div className="container mx-auto max-w-3xl text-center">
          <Heart className="h-9 w-9 text-[#FFB7B2] mx-auto mb-5" />
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">You Deserve Sunshine After the Rain</h2>
          <p className="text-white/70 mb-7 max-w-xl mx-auto text-sm">Start for free. Stay because it works. — Brian Bass, ThryvLife</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/chat"><Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white px-8 py-5 rounded-full font-semibold" data-testid="final-chat-btn">Talk to Hope (Free) <MessageCircle className="ml-2 h-5 w-5" /></Button></Link>
            <Button onClick={scrollToPricing} variant="outline" className="rounded-full px-8 py-5 text-white border-2 border-white/20 hover:bg-white/10 font-medium" data-testid="final-pricing-btn">View Plans</Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 lg:px-12 bg-[#0F1A26] text-white" data-testid="footer">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="font-heading text-xl font-bold mb-2">ThryvLife</div>
              <p className="text-white/50 text-xs leading-relaxed">Built by Brian Bass (BriVonB)... a survivor fighting for survivors... because exponential growth does not come from incremental thinking... it comes from incremental doing... one step... one day... one decision at a time... and before you know it... you are standing in the life you built from nothing.</p>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3">Quick Links</h4>
              <nav className="flex flex-col gap-1.5">
                <Link to="/about" className="text-white/60 hover:text-white text-xs">About Brian</Link>
                <Link to="/chat" className="text-white/60 hover:text-white text-xs">AI Companion (Free)</Link>
                <Link to="/resources" className="text-white/60 hover:text-white text-xs">Emergency Resources</Link>
                <Link to="/bonus" className="text-white/60 hover:text-white text-xs">Free PDF Guide</Link>
                <Link to="/login" className="text-white/60 hover:text-white text-xs">Sign In</Link>
              </nav>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-3">Crisis Hotlines</h4>
              <div className="space-y-1.5 text-xs text-white/60">
                <p>988 Suicide & Crisis: <span className="text-white">988</span></p>
                <p>DV Hotline: <span className="text-white">1-800-799-7233</span></p>
                <p>AR Crisis: <span className="text-white">1-800-367-0955</span></p>
                <p>Crisis Text: <span className="text-white">HOME to 741741</span></p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-white/40 text-[10px]">&copy; 2025 ThryvLife. All rights reserved.</p>
            <p className="text-white/30 text-[10px] max-w-lg text-center">Disclaimer: ThryvLife provides educational resources and peer support tools. We do not diagnose, treat, or cure any medical or mental health conditions.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
